// 添加到新文件 hooks/useTimezoneConverter.js

import { useState, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTimeZone, getTimezoneByMode, TIMEZONE_OPTIONS } from "../contexts/TimeZoneContext";
import { convertFromUTC, convertToUTC, formatTimeWithZone, getTimezoneAwareCrossDayMark, getDayCrossingDifference, formatWithCrossDayMark, isSameDay, createUtcTimeString, syncFlightTimePoints, handleFlightDateChange } from "../utils/timezoneUtils";

// 配置dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 时区转换钩子函数
 * 提供当前选择时区下的时区转换功能
 *
 * @param {string} airportCode - 机场代码（用于LOCAL模式）
 * @param {Object} airportsMap - 机场数据映射（用于LOCAL模式）
 * @returns {Object} 时区转换工具函数集合
 */
const useTimezoneConverter = (airportCode = null, airportsMap = {}) => {
    const { timeZone, isUTC, isBeijing, isLocal } = useTimeZone();

    // 获取当前使用的实际时区
    const currentTimezone = useMemo(() => {
        return getTimezoneByMode(timeZone, airportCode, airportsMap);
    }, [timeZone, airportCode, airportsMap]);

    /**
     * 从UTC时间转换为当前选择的时区
     * @param {string|Date|dayjs} utcTime - UTC时间
     * @param {string} format - 可选，如果提供则返回格式化字符串
     * @returns {dayjs|string} dayjs对象或格式化的字符串
     */
    const fromUTC = (utcTime, format = null) => {
        if (!utcTime) return format ? "" : null;

        try {
            const time = dayjs.utc(utcTime).tz(currentTimezone);
            return format ? time.format(format) : time;
        } catch (error) {
            console.error("时区转换错误:", error);
            return format ? "" : dayjs(utcTime);
        }
    };

    /**
     * 将当前时区的时间转换为UTC
     * @param {string|Date|dayjs} localTime - 当前时区的时间
     * @param {string} format - 可选，如果提供则返回格式化字符串
     * @returns {dayjs|string} UTC时间的dayjs对象或格式化的字符串
     */
    const toUTC = (localTime, format = null) => {
        if (!localTime) return format ? "" : null;

        try {
            // 假设输入的localTime是当前时区的时间
            const time = dayjs.tz(localTime, currentTimezone).utc();
            return format ? time.format(format) : time;
        } catch (error) {
            console.error("UTC转换错误:", error);
            return format ? "" : dayjs(localTime).utc();
        }
    };

    /**
     * 检查两个日期之间是否跨天
     * @param {string} utcDate1 - UTC日期时间1
     * @param {string} utcDate2 - UTC日期时间2
     * @returns {number} 跨天天数 (0表示同一天，1表示相差一天，以此类推，负数表示日期2早于日期1)
     */
    const checkCrossDay = (utcDate1, utcDate2) => {
        if (!utcDate1 || !utcDate2) return 0;

        try {
            const date1 = fromUTC(utcDate1).startOf("day");
            const date2 = fromUTC(utcDate2).startOf("day");
            return date2.diff(date1, "day");
        } catch (error) {
            console.error("跨天检查错误:", error);
            return 0;
        }
    };

    /**
     * 带跨天标记格式化时间
     * @param {string} utcTime - UTC时间
     * @param {string} format - 格式化模式
     * @param {string} referenceDate - 参考日期(UTC)，用于确定是否跨天
     * @returns {string} 带跨天标记的格式化时间
     */
    const formatWithDayMark = (utcTime, format = "HH:mm", referenceDate = null) => {
        if (!utcTime) return "";

        try {
            const formattedTime = fromUTC(utcTime, format);

            if (referenceDate) {
                const crossDays = checkCrossDay(referenceDate, utcTime);
                if (crossDays !== 0) {
                    return `${formattedTime} ${crossDays > 0 ? "+" : ""}${crossDays}`;
                }
            }

            return formattedTime;
        } catch (error) {
            console.error("格式化时间错误:", error);
            return "";
        }
    };

    // 判断两个时间是否在同一天
    const isSameDayInTimezone = (utcTime1, utcTime2) => {
        return isSameDay(utcTime1, utcTime2, currentTimezone);
    };

    // 创建UTC时间字符串
    const createUTCString = (date, time) => {
        return createUtcTimeString(date, time);
    };

    // 同步航班相关时间点
    const syncFlightTimes = (flight, newDate) => {
        return handleFlightDateChange(flight, newDate);
    };

    // 格式化显示航班时间
    const formatFlightTime = (utcTime, format = "HH:mm") => {
        return formatTimeWithZone(utcTime, currentTimezone, format);
    };

    // 获取跨天标记
    const getCrossDayMark = (utcStd, utcSta) => {
        return getTimezoneAwareCrossDayMark(utcStd, utcSta, currentTimezone);
    };

    /**
     * 获取当前时区信息
     * @returns {Object} 时区信息对象
     */
    const getTimezoneInfo = () => {
        // 基本信息
        const info = {
            id: currentTimezone,
            actual: currentTimezone,
            displayName: currentTimezone === "UTC" ? "UTC" : currentTimezone === "Asia/Shanghai" ? "北京时间" : `本地时间 (${currentTimezone})`,
            isUTC: currentTimezone === "UTC",
            isBeijing: currentTimezone === "Asia/Shanghai",
            isLocal: timeZone === TIMEZONE_OPTIONS.LOCAL,
            usingAirport: isLocal && airportCode ? airportCode : null,
            offsetString: null, // 后端提供的原始偏移 (+8, -5:30, Z)
            offsetDisplay: null, // 最终用于显示的UTC格式 (UTC+8, UTC-5:30, UTC+0)
            offsetMinutes: null,
        };

        // 尝试从机场数据获取时区偏移量
        if (isLocal && airportCode && airportsMap && airportsMap[airportCode]) {
            const airport = airportsMap[airportCode];

            if (airport.utc_offset_string) {
                info.offsetString = airport.utc_offset_string;
                info.offsetDisplay = airport.utc_offset_string === "Z" ? "UTC+0" : `UTC${airport.utc_offset_string}`;
                // 如果需要，可以从string反向计算minutes，但通常后端也会提供offset_minutes
            } else if (typeof airport.utc_offset_minutes === "number") {
                info.offsetMinutes = airport.utc_offset_minutes;
                if (airport.utc_offset_minutes === 0) {
                    info.offsetString = "+0"; // 或 "Z"
                    info.offsetDisplay = "UTC+0";
                } else {
                    const hours = Math.floor(Math.abs(airport.utc_offset_minutes) / 60);
                    const minutes = Math.abs(airport.utc_offset_minutes) % 60;
                    const sign = airport.utc_offset_minutes >= 0 ? "+" : "-";
                    info.offsetString = `${sign}${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, "0")}` : ""}`;
                    info.offsetDisplay = `UTC${info.offsetString}`;
                }
            }
        }

        // 如果没有从后端获取到偏移量，则尝试使用dayjs计算
        if (!info.offsetDisplay) {
            try {
                const now = dayjs().tz(currentTimezone);
                const offsetMinutes = now.utcOffset();
                info.offsetMinutes = offsetMinutes;

                if (offsetMinutes === 0) {
                    info.offsetString = "+0"; // 或 "Z"
                    info.offsetDisplay = "UTC+0";
                } else {
                    const hours = Math.floor(Math.abs(offsetMinutes) / 60);
                    const minutes = Math.abs(offsetMinutes) % 60;
                    const sign = offsetMinutes >= 0 ? "+" : "-";
                    const minuteStr = minutes > 0 ? `:${minutes.toString().padStart(2, "0")}` : "";
                    info.offsetString = `${sign}${hours}${minuteStr}`;
                    info.offsetDisplay = `UTC${info.offsetString}`;
                }
            } catch (error) {
                console.error("计算时区偏移量出错:", error);
                // 在错误情况下，offsetDisplay 可以保持为null或设置为一个表示错误的值
                info.offsetDisplay = currentTimezone; // 或者更明确的错误提示
            }
        }

        return info;
    };

    return {
        currentTimezone,
        fromUTC,
        toUTC,
        checkCrossDay,
        formatWithDayMark,
        isSameDayInTimezone,
        createUTCString,
        syncFlightTimes,
        formatFlightTime,
        getCrossDayMark,
        getTimezoneInfo,
    };
};

export default useTimezoneConverter;
